import { Button } from '@design/components/button/Button';
import { Input } from '@design/components/input/Input';
import LetterIcon from '@static/images/icons/letter.svg';
import { ToggleBrandLogo } from '@terminal/components/toggle-brand-logo/ToggleBrandLogo';
import { authPaths } from '@terminal/routes/app-paths';
import clsx from 'clsx';
import { ChangeEvent, FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import authStyles from '../auth-shared-styles/AuthSharedStyles.module.scss';
import styles from './ForgotPasswordView.module.scss';
import { useForgotPasswordState } from './useForgotPasswordState';

export const ForgotPasswordView = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const {
    email,
    clearError,
    validateEmail,
    updateEmail,
    postReqResetPassword,
    requestState: { isPending, isSuccess },
    errorText,
  } = useForgotPasswordState();
  const [successTabView, setSuccessTabView] = useState(false);

  const onChangeEmail = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    updateEmail(value);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (errorText || isPending) {
      return;
    }

    //Field validation
    const isValid = validateEmail(email);

    if (!isValid) {
      return;
    }

    setSuccessTabView(true);
    postReqResetPassword(email);
  };

  const loginRedirect = () => {
    navigate(authPaths.login);
  };

  const isLinkSent = isSuccess && successTabView;

  return (
    <div
      className={authStyles.layout}
      style={{ backgroundImage: `url('/app/static/images/auth/sun.png')` }}
    >
      <div className={styles.wrapper}>
        <div className={styles.container}>
          {!isLinkSent && (
            <ToggleBrandLogo
              className={authStyles.logo}
              alt="logo"
              title="logo"
            />
          )}
          <form
            className={styles.form}
            onSubmit={onSubmit}
            data-testid="forgot-password"
          >
            {isLinkSent ? (
              <div
                className={clsx(
                  authStyles.wrapperWithBg,
                  styles.emailSentWrapper
                )}
              >
                <LetterIcon className={styles.letterSvg} />
                <h1 className={styles.successTitle} data-testid="success-title">
                  {t('auth:forgotResetPassword.checkInbox')}
                </h1>
                <p
                  className={styles.successText}
                  data-testid="success-description"
                >
                  {t('auth:forgotResetPassword.detailedNote')}
                </p>
                <div className={styles.successEmailWrapper}>{email}</div>
                <Button
                  className={styles.backToLoginButton}
                  data-testid="back-to-login"
                  label={t('auth:forgotResetPassword.backToLogin')}
                  onClick={loginRedirect}
                  variant="tertiary"
                  size="large"
                />
              </div>
            ) : (
              <div
                className={clsx(
                  authStyles.wrapperWithBg,
                  styles.forgotPassWrapper
                )}
              >
                <h1 className={authStyles.heading}>
                  {t('auth:forgotResetPassword.heading')}
                </h1>
                <p className={styles.description}>
                  {t('auth:forgotResetPassword.provideEmailNote')}
                </p>
                <Input
                  id="email-input"
                  name="email-input"
                  type="text"
                  variant="outline"
                  onChange={onChangeEmail}
                  onFocus={clearError}
                  value={email}
                  hasError={!!errorText}
                  label={t('auth:forgotResetPassword.emailLabel')}
                  errorText={errorText}
                  data-testid="email-input"
                />
                <div className={authStyles.centerContent}>
                  <div className={authStyles.sendButtonWrapper}>
                    <Button
                      type="submit"
                      fullWidth
                      label={t('auth:forgotResetPassword.sendResetLink')}
                      isLoading={isPending}
                      data-testid="submit-button"
                      size="large"
                    />
                  </div>
                  <Link
                    to={authPaths.login}
                    className={authStyles.cancelButton}
                    data-testid="cancel-button"
                  >
                    {t('auth:forgotResetPassword.cancelButton')}
                  </Link>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};
